/**
 * Download Page
 *
 * Post-payment landing page. End users arrive here after completing Stripe Checkout.
 * The ONLY access point to the dashboard for end users is through the DTA (Desktop app).
 * This page shows the download link and brief setup instructions.
 *
 * Gating:
 *   - If user is not authenticated OR has no active/past_due subscription,
 *     redirect to /get-access (they need to pay first).
 *   - If the ?session_id= query param is present and the subscription isn't active yet,
 *     poll /bff/subscription briefly to account for webhook delay.
 *
 * Note: this page is NOT wrapped in ProtectedRoute — it handles its own gating to
 * avoid any flash of unauthed content or infinite redirect loops.
 */

import React, { useEffect, useState } from 'react';
import usePageTitle from '../../hooks/usePageTitle';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { API_BASE_URL, fetchWithCsrf } from '../../config/api';
import SiteHeader from '../../components/site/SiteHeader';
import SiteFooter from '../../components/site/SiteFooter';
import { GET_STARTED_TAGLINE, GET_STARTED_INTRO } from '../../components/site/getStartedContent';

// Setup steps (10-29). Anyone reading this page has already created the
// account and subscribed, so the steps start at the download. Download and
// install are separate steps on purpose: the antivirus pause (Virus Scan,
// below) happens between them, and "Download" is the installer link itself.
const setupSteps = (email) => [
  <>
    <a href={DTA_DOWNLOAD_URL} className="text-[#2F5F9E] underline">Download</a> the desktop app.
  </>,
  <>Open your Downloads folder and double-click the No / SCAM installer (.exe) to install it.</>,
  <>Sign in with the same email you used to subscribe{email ? ` (${email})` : ''}.</>,
  <>Open the web dashboard from the desktop app when you need advanced features.</>,
];

// Mock download URL for now. Will be replaced by a real signed URL or file serve in Phase 2.
const DTA_DOWNLOAD_URL = '/downloads/nuvola-sci-setup.exe';

// How long to poll for webhook to land after returning from Stripe Checkout
const WEBHOOK_POLL_MAX_MS = 10000;
const WEBHOOK_POLL_INTERVAL_MS = 1500;

/** Page chrome, so the post-purchase landing looks like the same product. */
const Shell = ({ children }) => (
  <div className="flex min-h-screen flex-col bg-white">
    <div className="sticky top-0 z-50">
      <SiteHeader />
    </div>
    <main className="flex-1">{children}</main>
    <SiteFooter />
  </div>
);

const WelcomePage = () => {
  usePageTitle('Welcome');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { loading, authenticated, subscription, user, refetch } = useSubscription();

  const [portalLoading, setPortalLoading] = useState(false);
  const [portalError, setPortalError] = useState(null);
  const [checkingSubscription, setCheckingSubscription] = useState(false);

  // If we arrived with ?session_id= from Stripe Checkout success redirect and the
  // webhook hasn't landed yet, poll /bff/subscription briefly. This smooths out the
  // common case where Stripe's redirect beats their webhook by a second or two.
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (!sessionId) return;
    if (loading) return;
    if (subscription?.status === 'active' || subscription?.status === 'past_due') return;

    console.log('[WelcomePage] Post-checkout landing, polling for webhook...');
    setCheckingSubscription(true);

    const started = Date.now();
    const interval = setInterval(async () => {
      if (Date.now() - started > WEBHOOK_POLL_MAX_MS) {
        clearInterval(interval);
        setCheckingSubscription(false);
        console.warn('[WelcomePage] Webhook poll timeout — subscription still not active');
        return;
      }
      await refetch();
    }, WEBHOOK_POLL_INTERVAL_MS);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, subscription?.status, searchParams]);

  // Redirect unauthenticated or unsubscribed users. Wait for loading to finish and
  // for the webhook poll (if any) to complete before bouncing — otherwise the
  // happy path (Stripe just redirected here) would flash a redirect.
  useEffect(() => {
    if (loading || checkingSubscription) return;

    if (!authenticated) {
      const returnUrl = encodeURIComponent(window.location.pathname + window.location.search);
      window.location.href = `${API_BASE_URL}/auth/login?returnUrl=${returnUrl}`;
      return;
    }

    const status = subscription?.status;
    if (status !== 'active' && status !== 'past_due') {
      // User is authed but not paid — route them to checkout
      navigate('/get-access');
    }
  }, [loading, authenticated, subscription, checkingSubscription, navigate]);

  // Open the Stripe Customer Portal
  // A plan can be active without a Stripe customer (dev/mock activations never
  // touch Stripe), so explain that instead of telling a subscriber to subscribe.
  const billingUnavailableReason = subscription?.stripeCustomerId
    ? null
    : subscription?.source === 'mock'
      ? 'This plan was activated for development and isn’t linked to Stripe, so there is no billing portal for it.'
      : subscription?.status === 'active'
        ? 'This plan isn’t linked to a Stripe customer, so the billing portal can’t be opened. Contact support to reconnect billing.'
        : null;

  const openBillingPortal = async () => {
    setPortalLoading(true);
    setPortalError(null);
    try {
      const res = await fetchWithCsrf(`${API_BASE_URL}/payment/portal`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.message || 'Failed to open billing portal');
      }
      window.location.href = data.url;
    } catch (err) {
      console.error('[WelcomePage] Portal error:', err);
      setPortalError(err.message);
      setPortalLoading(false);
    }
  };

  // Loading + polling states
  if (loading || checkingSubscription) {
    return (
      <Shell>
        <div className="flex min-h-[60vh] items-center justify-center px-4">
          <div className="text-center">
            <div className="mx-auto h-10 w-10 animate-spin rounded-full border-b-2 border-[#2F5F9E]" />
            <p className="mt-4 text-[15px] text-black/70">
              {checkingSubscription ? 'Confirming your payment\u2026' : 'Loading\u2026'}
            </p>
          </div>
        </div>
      </Shell>
    );
  }

  // Bounce cases — redirect already dispatched; show spinner while it happens
  if (!authenticated || (subscription?.status !== 'active' && subscription?.status !== 'past_due')) {
    return (
      <Shell>
        <div className="flex min-h-[60vh] items-center justify-center px-4">
          <div className="text-center">
            <div className="mx-auto h-10 w-10 animate-spin rounded-full border-b-2 border-[#2F5F9E]" />
            <p className="mt-4 text-[15px] text-black/70">Redirecting\u2026</p>
          </div>
        </div>
      </Shell>
    );
  }

  // Happy path — paid user, show download UI
  // Single tier — the plan label is just the billing period. Legacy athena-*
  // codes still resolve so an older subscriber sees a real label.
  const planLabel = subscription?.billingPeriod === 'year' ? 'Annual' : 'Monthly';

  return (
    <Shell>
      <div className="mx-auto w-full max-w-[900px] px-4 pb-16 pt-10 md:px-8">
        {/* Confirmation */}
        <div className="border border-black/25 px-6 py-6 md:px-10 md:py-8">
          <div className="flex items-start gap-4">
            <span className="mt-1 flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-[#2F6B2F]">
              <svg className="h-5 w-5" fill="none" stroke="#FFFFFF" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
              </svg>
            </span>
            <div>
              <h1 className="text-[26px] font-bold leading-tight text-black md:text-[36px]">
                You&rsquo;re all set
              </h1>
              <p className="mt-1 text-[15px] text-black/70 md:text-[17px]">
                Your {planLabel} plan is active.
              </p>
            </div>
          </div>
        </div>

        {/* Easy to use. Easy to love. (10-28: the Get Access intro) */}
        <div className="mt-8">
          <h2 className="text-[22px] font-bold text-black md:text-[28px]">
            / {GET_STARTED_TAGLINE}
          </h2>
          <p className="mt-3 max-w-[62ch] text-[15px] leading-relaxed text-black md:text-[17px]">
            {GET_STARTED_INTRO}
          </p>
          <p className="mt-3 text-[15px] leading-relaxed text-black md:text-[17px]">
            No / SCAM runs as a desktop application on Windows. Download it, sign in, and the
            web dashboard opens automatically.
          </p>
        </div>

        {/* Setup steps (10-29: straight after the intro, so the page reads
            how to start, what you get, then what antivirus will do). */}
        <div className="mt-10">
          <h2 className="text-[20px] font-bold text-black md:text-[24px]">/ Setup steps.</h2>
          <ol className="mt-4 space-y-4">
            {setupSteps(user?.email).map((step, index) => (
              <li key={index} className="flex items-start gap-4">
                <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-[#D9D9D9] text-[14px] font-bold text-black">
                  {index + 1}
                </span>
                <span className="text-[15px] leading-snug text-black md:text-[17px]">{step}</span>
              </li>
            ))}
          </ol>

        </div>

        {/* Virus scan (10-07): what to expect from antivirus on first run. */}
        <div className="mt-10 border-t border-black/20 pt-8">
          <h2 className="text-[22px] font-bold text-black md:text-[28px]">/ Virus Scan.</h2>
          <p className="mt-3 text-[15px] leading-relaxed text-black md:text-[17px]">
            No / SCAM is a downloadable executable file (.exe). To protect your computer from
            suspicious malware, viruses or adware, your antivirus software will automatically kick
            off a scan of the No / SCAM application. Typically, it takes Norton or McAfee about 60
            seconds to inspect No / SCAM&rsquo;s digital signature, determining it is from a
            reputable company and safe, before allowing you to continue with the No / SCAM
            installation.
          </p>
          {/* The download sits after the virus-scan note (10-29): read the
              steps and what antivirus will do, then start. */}
          <a
            href={DTA_DOWNLOAD_URL}
            className="mt-8 block w-full rounded-[8px] bg-[#2F5F9E] px-6 py-4 text-center text-[17px] text-white transition-colors hover:bg-[#27508a] md:text-[19px]"
          >
            Download the Desktop App
          </a>
          <p className="mt-2 text-center text-[13px] text-black/60">
            Windows installer (.exe) &middot; Requires Windows 10 or later
          </p>
          <p className="mt-2 text-center text-[13px] text-black/60">
            Sign in with the same email you used to subscribe ({user?.email || 'your email'}).
          </p>
        </div>

        {/* Billing */}
        <div className="mt-10 border-t border-black/20 pt-8">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-[17px] font-bold text-black md:text-[19px]">
                Manage your subscription
              </p>
              <p className="mt-1 text-[15px] text-black/70">
                Update payment, view invoices, cancel, or change plans.
              </p>
            </div>
            <button
              type="button"
              onClick={openBillingPortal}
              disabled={portalLoading || !subscription?.stripeCustomerId}
              className="flex-shrink-0 rounded-[8px] border border-black/30 px-6 py-3 text-[16px] text-black transition-colors hover:bg-black/[.04] disabled:cursor-not-allowed disabled:opacity-50"
              title={billingUnavailableReason || ''}
            >
              {portalLoading ? 'Opening\u2026' : 'Manage billing'}
            </button>
          </div>
          {billingUnavailableReason && (
            <p className="mt-3 text-[14px] text-black/60">{billingUnavailableReason}</p>
          )}
          {portalError && !billingUnavailableReason && (
            <p className="mt-3 text-[14px] text-[#B02418]">{portalError}</p>
          )}
        </div>

        <p className="mt-10 text-center text-[14px] text-black/60">
          Need help? Contact{' '}
          <a href="mailto:support@nuvolaai.com" className="text-[#2F5F9E] underline">
            support@nuvolaai.com
          </a>
        </p>
      </div>
    </Shell>
  );
};

export default WelcomePage;
